import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useUser } from 'context/user-context';
import { GET_ASSISTED_INSTALLATION_WORKSPACE, GET_USER_ROLE } from 'gql/queries';
import { REQUEST_ASSISTED_INSTALLATION } from 'gql/mutations';
import { assistanceActive, assistanceUuid, assistanceDomain, readAssistanceWorkspace } from 'utils/assistedInstallation';
import type { AssistanceWorkspace } from 'types/assistedInstallation';

const CLOSED_REQUEST_NOTICE = 'Your earlier installation request is closed. Submit a new request if you still need assistance.';

export function useAssistedInstallation(storeId: string, domain: string, canManage: boolean, blocked: boolean) {
  const client = useApolloClient(); const { user } = useUser();
  const globalAdmin = user?.defaultRole === 'shipaid_admin';
  const scope = [user?.id, storeId, domain, canManage, globalAdmin].join('|');
  const generation = useRef(0), activeScope = useRef(scope), lock = useRef(false);
  const activeClient = useRef(client); activeClient.current = client;
  const gate = useRef({ canManage, blocked }); gate.current = { canManage, blocked }; activeScope.current = scope;
  const attempt = useRef<{ scope: string; key: string; callRequested: boolean;
    acknowledgement: { id: string; revision: number } | null } | null>(null);
  const initial = { scope, client, workspace: null as AssistanceWorkspace | null, loading: true, busy: false,
    needsReload: false, error: '', notice: '' };
  const [state, setState] = useState(initial);
  const visible = state.scope === scope && state.client === client ? state : initial;
  const read = useCallback(async () => {
    const result = await client.query({ query: GET_ASSISTED_INSTALLATION_WORKSPACE, variables: { input: { storeId } },
      fetchPolicy: 'no-cache', context: { queryDeduplication: false } });
    const workspace = readAssistanceWorkspace(result.data?.assistedInstallationWorkspace, storeId, domain);
    if (!workspace) throw new Error('Unconfirmed assistance scope');
    return workspace;
  }, [client, storeId, domain]);
  const refresh = useCallback(async () => {
    if (lock.current || !assistanceUuid(user?.id) || !assistanceUuid(storeId) || !assistanceDomain(domain)) return;
    lock.current = true; const sequence = ++generation.current;
    const current = () => activeClient.current === client && activeScope.current === scope && generation.current === sequence;
    setState(previous => ({ ...previous, scope, client, loading: true, busy: true, error: '', notice: '' }));
    try {
      const workspace = await read(); if (!current()) return;
      const acknowledged = attempt.current?.scope === scope ? attempt.current.acknowledgement : null;
      const reconciled = !!acknowledged && !!workspace.request && workspace.request.id === acknowledged.id
        && workspace.request.revision >= acknowledged.revision && !assistanceActive(workspace.request);
      if (reconciled) attempt.current = null;
      setState(previous => ({ ...previous, scope, workspace, needsReload: false,
        notice: reconciled ? CLOSED_REQUEST_NOTICE : '' }));
    } catch {
      if (current()) setState(previous => ({ ...previous, workspace: null, needsReload: true,
        error: 'Installation requests are unavailable. Reload to check again.' }));
    } finally {
      if (current()) { lock.current = false; setState(previous => ({ ...previous, loading: false, busy: false })); }
    }
  }, [client, read, scope, user?.id, storeId, domain]);
  useEffect(() => {
    const scopeGeneration = generation; generation.current++; lock.current = false; attempt.current = null;
    setState({ scope, client, workspace: null, loading: false, busy: false, needsReload: false, error: '', notice: '' });
    void refresh();
    return () => { scopeGeneration.current++; };
  }, [scope, client, refresh]);
  const submit = async (callRequested: boolean) => {
    if (lock.current || !gate.current.canManage || gate.current.blocked || visible.loading || visible.needsReload
      || !visible.workspace?.available || assistanceActive(visible.workspace.request) || typeof callRequested !== 'boolean') return;
    lock.current = true; const sequence = ++generation.current;
    const current = () => activeClient.current === client && activeScope.current === scope && generation.current === sequence;
    setState(previous => ({ ...previous, busy: true, error: '', notice: '' }));
    try {
      const preflight = await read(); if (!current()) return;
      const role = globalAdmin ? true : await client.query({ query: GET_USER_ROLE, variables: { storeId, userId: user!.id },
        fetchPolicy: 'no-cache', context: { queryDeduplication: false } }).then(result => Array.isArray(result.data?.storeRoles)
          && result.data.storeRoles.some((record: { role?: string }) => ['ADMIN', 'MANAGER'].includes(record.role || '')));
      if (!current()) return;
      if (!role) { setState(previous => ({ ...previous, needsReload: true, error: 'The store permission changed. Reload before requesting assistance.' })); return; }
      if (!preflight.available) throw new Error('Assistance capability unavailable');
      if (assistanceActive(preflight.request)) { setState(previous => ({ ...previous, workspace: preflight })); return; }
      if (!gate.current.canManage || gate.current.blocked) return;
      if (!attempt.current || attempt.current.scope !== scope) {
        const key = window.crypto?.randomUUID?.(); if (!assistanceUuid(key)) throw new Error('Request key unavailable');
        attempt.current = { scope, key, callRequested, acknowledgement: null };
      }
      const input = { storeId, requestKey: attempt.current.key, callRequested: attempt.current.callRequested };
      const result = await client.mutate({ mutation: REQUEST_ASSISTED_INSTALLATION, variables: { input } });
      if (!current()) return;
      const acknowledgement = readAssistanceWorkspace(result.data?.requestAssistedInstallation, storeId, domain);
      if (!acknowledgement?.request || !acknowledgement.available) throw new Error('Unconfirmed request acknowledgement');
      attempt.current.acknowledgement = { id: acknowledgement.request.id, revision: acknowledgement.request.revision };
      const workspace = await read(); if (!current()) return;
      const sameRequest = workspace.request?.id === acknowledgement.request.id;
      if (!workspace.available || !workspace.request || (sameRequest && workspace.request.revision < acknowledgement.request.revision)
        || (!sameRequest && assistanceActive(acknowledgement.request))) throw new Error('Unconfirmed request readback');
      const closed = !assistanceActive(acknowledgement.request) || !assistanceActive(workspace.request);
      attempt.current = null;
      setState(previous => ({ ...previous, workspace, notice: closed ? CLOSED_REQUEST_NOTICE : 'Installation request recorded.', needsReload: false }));
    } catch {
      if (current()) setState(previous => ({ ...previous, needsReload: true,
        error: 'The installation request could not be confirmed. Reload its status before trying again.' }));
    } finally {
      if (current()) { lock.current = false; setState(previous => ({ ...previous, busy: false })); }
    }
  };
  return { ...visible, refresh, submit, pendingCall: attempt.current?.scope === scope ? attempt.current.callRequested : null,
    retryEarlier: attempt.current?.scope === scope && !!visible.workspace?.request && !assistanceActive(visible.workspace.request),
    canSubmit: canManage && !blocked && !visible.loading && !visible.busy && !visible.needsReload
      && visible.workspace?.available === true && !assistanceActive(visible.workspace.request) };
}
